import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../../../api/client";
import useClientContext from "../../../../../contexts/useClientContext";
import useClientPageTitle from "../../../../../contexts/useClientPageTitle";
import useImpersonationBlockContext from "../../../../../contexts/useImpersonationBlockContext";
import { pageRoutes } from "../../../../../routes";
import { workspaceInvestorSelector } from "../../../../../store/state/user/selectors";
import SettingsWrapper from "../../SettingsWrapper";
import ChangesWarningAlert from "../../common/ChangesWarningAlert";
import { useBankSettingsNavigation } from "../navigation";
import BankAccountSelector from "./BankAccountSelector";

const updateFundAssignments = withErrorHandling(api.bankAccounts.assignBankAccountsToFundInvestors);

const ChangeFundAssignmentsPage = () => {
  useClientPageTitle("Change Assignments");

  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { isBlockedUnderImpersonation, showImpersonationBlockedDialog } = useImpersonationBlockContext();
  const workspaceInvestor = useSelector(workspaceInvestorSelector);

  const { changeFundAssignmentsPageNavigationState: navigationState, navigateToBankAccountsPage } =
    useBankSettingsNavigation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState(navigationState?.bankAccountId || "");
  const [comment, setComment] = useState("");

  const handleBackButtonClick = () => {
    navigateToBankAccountsPage();
  };

  const handleSubmit = async () => {
    if (!navigationState) {
      return;
    }

    setIsSubmitting(true);
    const [, error] = await updateFundAssignments({
      assignments: [{ fundInvestorId: navigationState.fundInvestorId, bankAccountId: selectedBankAccountId }],
      commentUserInput: comment.trim() || undefined,
    });
    setIsSubmitting(false);

    if (error) {
      if (isBlockedUnderImpersonation(error)) {
        showImpersonationBlockedDialog();
        return;
      }
      logError(error, "[ChangeFundAssignmentsPage] handleSubmit");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("The request has been submitted");
    setTimeout(() => navigateToBankAccountsPage(), 100);
  };

  if (!navigationState) {
    return <Navigate to={`/${clientCode}/${pageRoutes.settings}/lp/bank`} />;
  }

  const handleSelectedBankAccountChange = (bankAccountId: string) => {
    setSelectedBankAccountId(bankAccountId);
  };

  return (
    <SettingsWrapper
      title="Change Assignments"
      subtitle={workspaceInvestor?.investorTitle}
      scrollOwner="wrapper"
      onBackButtonClick={handleBackButtonClick}
      Footer={
        <Box sx={{ py: 2, px: 3, display: "flex", justifyContent: "space-between" }}>
          <HorizontalFill />
          <Button
            loading={isSubmitting}
            variant="contained"
            onClick={handleSubmit}
            disabled={navigationState?.bankAccountId === selectedBankAccountId}
          >
            Send Request
          </Button>
        </Box>
      }
    >
      <Stack spacing={3} sx={{ pb: 2 }}>
        <ChangesWarningAlert />
        <Typography variant="subtitle1">{navigationState.fundName}</Typography>
        <BankAccountSelector
          bankAccounts={navigationState.bankAccounts}
          selectedBankAccountId={selectedBankAccountId}
          onSelectedBankAccountChange={handleSelectedBankAccountChange}
        />
        <Stack spacing={2}>
          <Typography variant="subtitle2">Comment</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            spellCheck
            placeholder="Add an optional comment for the reviewer..."
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </Stack>
    </SettingsWrapper>
  );
};

export default ChangeFundAssignmentsPage;
